import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { PlayerProvider } from '@/contexts/PlayerContext';
import { PlayerAuthProvider } from '@/contexts/PlayerAuthContext';
import { CmsAuthProvider, useCmsAuth } from '@/contexts/CmsAuthContext';
import { ConsentProvider } from '@/contexts/ConsentContext';
import { AppShell } from '@/components/layout/AppShell';
import { CmsShell } from '@/components/cms/CmsShell';
import { Toaster } from '@/components/ui/sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { CookieConsentBanner } from '@/components/shared/CookieConsentBanner';
import { CookiePreferencesModal } from '@/components/shared/CookiePreferencesModal';
import { ConfirmProvider } from '@/components/shared/ConfirmDialog';
import { SupportBubble } from '@/components/support/SupportBubble';
import { AnalyticsGate } from '@/components/analytics/AnalyticsGate';
import { BILLING_HIDDEN_SOFT_LAUNCH } from '@/lib/softLaunchFlags';

/* ──────────────────────────────────────────────────────────────────────────
   First-paint critical routes — loaded eagerly so the marketing/login
   experience renders without a Suspense flash. Everything else is split
   into its own chunk via React.lazy() and fetched on demand.
   ────────────────────────────────────────────────────────────────────────── */
import LandingPage from '@/pages/LandingPage';
import UnifiedAuthPage from '@/pages/UnifiedAuthPage';
import GatekeeperPage from '@/pages/player/GatekeeperPage';

/* Admin / Client (gated by login) */
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const TournamentsPage = lazy(() => import('@/pages/TournamentsPage'));
const AdminGridPage = lazy(() => import('@/pages/AdminGridPage'));
const BrandingPage = lazy(() => import('@/pages/BrandingPage'));
const BillingPage = lazy(() => import('@/pages/BillingPage'));
const BillingComingSoonPage = lazy(() => import('@/pages/BillingComingSoonPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const SeasonLeaderboardPage = lazy(() => import('@/pages/SeasonLeaderboardPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const AdminLiveScoresPage = lazy(() => import('@/pages/AdminLiveScoresPage'));
const AdminLmsPoolPage = lazy(() => import('@/pages/AdminLmsPoolPage'));
const AdminRacingPoolPage = lazy(() => import('@/pages/AdminRacingPoolPage'));

/* Auxiliary public pages */
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const RulesPage = lazy(() => import('@/pages/RulesPage'));
const ConfirmEmailPage = lazy(() => import('@/pages/ConfirmEmailPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const CookiePolicyPage = lazy(() => import('@/pages/CookiePolicyPage'));

/* Marketing site — lazy so the home page bundle stays lean */
const PoolTypesPage = lazy(() => import('@/pages/marketing/PoolTypesPage'));
const GolfSocietiesPage = lazy(() => import('@/pages/marketing/GolfSocietiesPage'));
const SportsClubsPage = lazy(() => import('@/pages/marketing/SportsClubsPage'));
const BarsAndPubsPage = lazy(() => import('@/pages/marketing/BarsAndPubsPage'));
const WorkplacesPage = lazy(() => import('@/pages/marketing/WorkplacesPage'));
const FriendGroupsPage = lazy(() => import('@/pages/marketing/FriendGroupsPage'));
const MastersPoolsPage = lazy(() => import('@/pages/marketing/MastersPoolsPage'));
const VsSpreadsheetsPage = lazy(() => import('@/pages/marketing/VsSpreadsheetsPage'));
const GuideRunSweepstakePage = lazy(() => import('@/pages/marketing/GuideRunSweepstakePage'));
const FaqPage = lazy(() => import('@/pages/marketing/FaqPage'));

/* CMS (Super Admin) — all lazy; a regular user never downloads any of this */
const CmsLoginPage = lazy(() => import('@/pages/cms/CmsLoginPage'));
const CmsDashboardPage = lazy(() => import('@/pages/cms/CmsDashboardPage'));
const CmsTournamentsPage = lazy(() => import('@/pages/cms/CmsTournamentsPage'));
const CmsTournamentEditPage = lazy(() => import('@/pages/cms/CmsTournamentEditPage'));
const CmsPlayersPage = lazy(() => import('@/pages/cms/CmsPlayersPage'));
const CmsClientsPage = lazy(() => import('@/pages/cms/CmsClientsPage'));
const CmsClientDetailPage = lazy(() => import('@/pages/cms/CmsClientDetailPage'));
const CmsTiersPage = lazy(() => import('@/pages/cms/CmsTiersPage'));
const CmsSettingsPage = lazy(() => import('@/pages/cms/CmsSettingsPage'));
const CmsEmailTemplatesPage = lazy(() => import('@/pages/cms/CmsEmailTemplatesPage'));
const CmsNewslettersPage = lazy(() => import('@/pages/cms/CmsNewslettersPage'));
const CmsApiMonitorPage = lazy(() => import('@/pages/cms/CmsApiMonitorPage'));
const CmsReportsPage = lazy(() => import('@/pages/cms/CmsReportsPage'));
const CmsSupportPage = lazy(() => import('@/pages/cms/CmsSupportPage'));
const CmsBetaAccessPage = lazy(() => import('@/pages/cms/CmsBetaAccessPage'));
const CmsInviteCodesPage = lazy(() => import('@/pages/cms/CmsInviteCodesPage'));

/* Player-facing — gatekeeper is eager (often first paint for shared links),
   everything else is split. Especially TV/ESPN leaderboards, which carry
   their own polling/animation code, are big wins to split off. */
const WarRoomPage = lazy(() => import('@/pages/player/WarRoomPage'));
const LmsWarRoomPage = lazy(() => import('@/pages/player/LmsWarRoomPage'));
const RacingWarRoomPage = lazy(() => import('@/pages/player/RacingWarRoomPage'));
const PaymentSuccessPage = lazy(() => import('@/pages/player/PaymentSuccessPage'));
// PlayerLivePage removed in favour of per-pool live-scoring CTAs on
// each dashboard card. /player/live now redirects to /player/dash (see
// Routes below) so legacy bookmarks keep working.
const SweatPage = lazy(() => import('@/pages/player/SweatPage'));
const TVLeaderboardPage = lazy(() => import('@/pages/player/TVLeaderboardPage'));
const LmsPickPage = lazy(() => import('@/pages/player/LmsPickPage'));
const LmsTvBoardPage = lazy(() => import('@/pages/LmsTvBoardPage'));
const ESPNLeaderboardPage = lazy(() => import('@/pages/player/ESPNLeaderboardPage'));
const PlayerDashboardPage = lazy(() => import('@/pages/player/PlayerDashboardPage'));
const PlayerProfilePage = lazy(() => import('@/pages/player/PlayerProfilePage'));

/* ────────────────────────────────────────────────────────────────────────── */

const RouteSkeleton = () => (
  <div className="flex items-center justify-center min-h-screen" data-testid="route-suspense-fallback">
    <div className="space-y-4 w-64">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-64" />
      <Skeleton className="h-4 w-40" />
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <RouteSkeleton />;
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
};

const CmsProtectedRoute = ({ children }) => {
  const { user, loading } = useCmsAuth();
  if (loading) return <RouteSkeleton />;
  if (!user) return <Navigate to="/cms/login" replace />;
  return <CmsShell>{children}</CmsShell>;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PlayerAuthProvider>
          <PlayerProvider>
            <CmsAuthProvider>
              <ConsentProvider>
                <ConfirmProvider>
                  <BrowserRouter>
                  <Suspense fallback={<RouteSkeleton />}>
                  <Routes>
                    {/* Public */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/login" element={<UnifiedAuthPage />} />
                    {/* `/signup` is a friendlier shareable URL for invite-code
                        links — it maps to the same component as /login but the
                        page reads `mode=signup` from the URL to land on the
                        signup form. */}
                    <Route path="/signup" element={<UnifiedAuthPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/confirm-email" element={<ConfirmEmailPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/cookie-policy" element={<CookiePolicyPage />} />
                    <Route path="/rules" element={<RulesPage />} />

                    {/* Marketing site */}
                    <Route path="/pool-types" element={<PoolTypesPage />} />
                    <Route path="/golf-societies" element={<GolfSocietiesPage />} />
                    <Route path="/sports-clubs" element={<SportsClubsPage />} />
                    <Route path="/bars-and-pubs" element={<BarsAndPubsPage />} />
                    <Route path="/workplaces" element={<WorkplacesPage />} />
                    <Route path="/friend-groups" element={<FriendGroupsPage />} />
                    <Route path="/masters-pools" element={<MastersPoolsPage />} />
                    <Route path="/vs-spreadsheets" element={<VsSpreadsheetsPage />} />
                    <Route path="/guides/run-a-sweepstake" element={<GuideRunSweepstakePage />} />
                    <Route path="/faq" element={<FaqPage />} />

                    {/* Admin/Client Protected */}
                    <Route path="/admin/dash" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                    <Route path="/admin/tournaments" element={<ProtectedRoute><TournamentsPage /></ProtectedRoute>} />
                    <Route path="/admin/tournaments/:activationId" element={<ProtectedRoute><AdminGridPage /></ProtectedRoute>} />
                    <Route path="/admin/branding" element={<ProtectedRoute><BrandingPage /></ProtectedRoute>} />
                    <Route path="/admin/billing" element={<ProtectedRoute>{BILLING_HIDDEN_SOFT_LAUNCH ? <BillingComingSoonPage /> : <BillingPage />}</ProtectedRoute>} />
                    <Route path="/admin/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                    <Route path="/admin/season" element={<ProtectedRoute><SeasonLeaderboardPage /></ProtectedRoute>} />
                    <Route path="/admin/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                    <Route path="/admin/live" element={<ProtectedRoute><AdminLiveScoresPage /></ProtectedRoute>} />
                    <Route path="/admin/lms/:poolId" element={<ProtectedRoute><AdminLmsPoolPage /></ProtectedRoute>} />
                    <Route path="/admin/racing/:poolId" element={<ProtectedRoute><AdminRacingPoolPage /></ProtectedRoute>} />

                    {/* CMS (Super Admin) */}
                    <Route path="/cms/login" element={<CmsLoginPage />} />
                    <Route path="/cms/dashboard" element={<CmsProtectedRoute><CmsDashboardPage /></CmsProtectedRoute>} />
                    <Route path="/cms/tournaments" element={<CmsProtectedRoute><CmsTournamentsPage /></CmsProtectedRoute>} />
                    <Route path="/cms/tournaments/:id" element={<CmsProtectedRoute><CmsTournamentEditPage /></CmsProtectedRoute>} />
                    <Route path="/cms/clients" element={<CmsProtectedRoute><CmsClientsPage /></CmsProtectedRoute>} />
                    <Route path="/cms/players" element={<CmsProtectedRoute><CmsPlayersPage /></CmsProtectedRoute>} />
                    <Route path="/cms/clients/:id" element={<CmsProtectedRoute><CmsClientDetailPage /></CmsProtectedRoute>} />
                    <Route path="/cms/tiers" element={<CmsProtectedRoute><CmsTiersPage /></CmsProtectedRoute>} />
                    <Route path="/cms/settings" element={<CmsProtectedRoute><CmsSettingsPage /></CmsProtectedRoute>} />
                    <Route path="/cms/emails" element={<CmsProtectedRoute><CmsEmailTemplatesPage /></CmsProtectedRoute>} />
                    <Route path="/cms/newsletters" element={<CmsProtectedRoute><CmsNewslettersPage /></CmsProtectedRoute>} />
                    <Route path="/cms/api-monitor" element={<CmsProtectedRoute><CmsApiMonitorPage /></CmsProtectedRoute>} />
                    <Route path="/cms/reports" element={<CmsProtectedRoute><CmsReportsPage /></CmsProtectedRoute>} />
                    <Route path="/cms/support" element={<CmsProtectedRoute><CmsSupportPage /></CmsProtectedRoute>} />
                    <Route path="/cms/beta-access" element={<CmsProtectedRoute><CmsBetaAccessPage /></CmsProtectedRoute>} />
                    <Route path="/cms/invite-codes" element={<CmsProtectedRoute><CmsInviteCodesPage /></CmsProtectedRoute>} />
                    <Route path="/cms" element={<Navigate to="/cms/dashboard" replace />} />

                    {/* Player-Facing */}
                    <Route path="/joinpool" element={<GatekeeperPage />} />
                    <Route path="/joinpool/:code" element={<GatekeeperPage />} />
                    <Route path="/joinpool/:code/picks" element={<WarRoomPage />} />
                    <Route path="/joinpool/:code/war-room" element={<WarRoomPage />} />
                    <Route path="/joinpool/:code/lms" element={<LmsWarRoomPage />} />
                    <Route path="/joinpool/:code/lms/pick" element={<LmsPickPage />} />
                    <Route path="/joinpool/:code/racing" element={<RacingWarRoomPage />} />
                    <Route path="/joinpool/:code/payment-success" element={<PaymentSuccessPage />} />
                    <Route path="/joinpool/:code/live" element={<SweatPage />} />
                    <Route path="/tv/:code" element={<TVLeaderboardPage />} />
                    <Route path="/tv/lms/:code" element={<LmsTvBoardPage />} />
                    <Route path="/tournament/:code" element={<ESPNLeaderboardPage />} />
                    <Route path="/player/dash" element={<PlayerDashboardPage />} />
                    {/* Legacy redirect: the standalone Live page was
                        removed in favour of per-pool live-scoring CTAs.
                        Keep this so old bookmarks / shared links land on
                        the dashboard instead of 404'ing. */}
                    <Route path="/player/live" element={<Navigate to="/player/dash" replace />} />
                    <Route path="/player/profile" element={<PlayerProfilePage />} />

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                  </Suspense>
                  <CookieConsentBanner />
                  <CookiePreferencesModal />
                  <SupportBubble />
                  <AnalyticsGate />
                </BrowserRouter>
                </ConfirmProvider>
                <Toaster position="bottom-right" richColors />
              </ConsentProvider>
            </CmsAuthProvider>
          </PlayerProvider>
        </PlayerAuthProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
